import { Link, PageWrapper } from "@/components";
import * as GlobalS from "@/components/styled";
import { FieldNames, FormType, GUTTER_SIZE } from "@/enums";
import { nameof, routes } from "@/helpers";
import { useExport } from "@/hooks";
import {
  IDynamicFormField,
  IDynamicFormValue,
  IDynamicLabel,
  IProjectTemplate,
} from "@/interfaces";
import { useFormPropertyProvider } from "@/providers/FormPropertyProvider";
import { useGetProjectTemplatesQuery } from "@/services";
import { FormStatus } from "@/types";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faFileExport } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Row, Space, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { Gutter } from "antd/lib/grid/row";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IProjectTemplateTableData {
  label: IDynamicLabel;
  status: FormStatus;
  projectTemplateId: string;
}

export const ProjectTemplates = () => {
  const intl = useIntl();

  const [exportFile, isExporting] = useExport({
    fileName: "ViperProjectTemplateMilestones",
    url: "api/v1/projectTemplates/exportMilestones",
    fileExtension: "xlsx",
    includeDate: true,
  });

  const { getFormProperties, isFormPropertyLoading } =
    useFormPropertyProvider();

  const [projectTypes, setProjectTypes] = useState<IDynamicFormValue[]>([]);
  const [dataSource, setDataSource] = useState<IProjectTemplateTableData[]>();

  const { data: projectTemplates, isFetching: fetchingProjectTemplates } =
    useGetProjectTemplatesQuery();

  useEffect(() => {
    if (isFormPropertyLoading) return;

    const form = getFormProperties(FormType.Intake);
    const projectTypeField = form.find(
      (field: IDynamicFormField) =>
        field.property.name === FieldNames.ProjectType
    );

    if (!projectTypeField) return;

    setProjectTypes(projectTypeField.values);
  }, [getFormProperties, isFormPropertyLoading]);

  useEffect(() => {
    if (!projectTemplates) return;

    const data: IProjectTemplateTableData[] = [];

    projectTypes.forEach((projectType) => {
      const projectTemplate = projectTemplates.find(
        (projectTemplate) => projectTemplate.projectTypeId === projectType.id
      );

      if (!projectTemplate?.id) {
        console.error(
          `Project template does not exists for project type ${projectType.label.fallback}`
        );
        return;
      }

      data.push({
        label: projectType.label,
        status: projectType.status,
        projectTemplateId: projectTemplate.id,
      });
    });

    setDataSource(data);
  }, [projectTypes, projectTemplates]);

  const columns: ColumnsType<IProjectTemplateTableData> = [
    {
      title: intl.formatMessage({ id: "projectTemplate.colProjectType" }),
      sorter: (a, b) =>
        a.label.fallback.toLowerCase() > b.label.fallback.toLowerCase()
          ? -1
          : 1,
      render: (projectType) => (
        <Link
          to={routes.admin.projectTemplates.edit(projectType.projectTemplateId)}
        >
          {projectType.label.fallback}
        </Link>
      ),
    },
    {
      title: intl.formatMessage({ id: "projectTemplate.colStatus" }),
      dataIndex: nameof<IProjectTemplate>("status"),
    },
  ];

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "projectTemplate.pageTitle" })}
    >
      <Row gutter={GUTTER_SIZE.large as [Gutter, Gutter]}>
        <GlobalS.PageContentCard
          title={intl.formatMessage({ id: "projectTemplate.tableTitle" })}
          extra={
            <Space>
              <Button
                data-test-id="export-xlsx-btn"
                onClick={exportFile}
                icon={<FontAwesomeIcon icon={faFileExport as IconProp} />}
                loading={isExporting}
              >
                {intl.formatMessage({ id: "projectTemplate.exportMilestones" })}
              </Button>
            </Space>
          }
        >
          <Table
            data-test-id="project-types-active-table"
            columns={columns}
            dataSource={dataSource}
            rowKey={({ projectTemplateId }): string => projectTemplateId}
            pagination={{ position: ["bottomRight"] }}
            loading={isFormPropertyLoading || fetchingProjectTemplates}
            scroll={{ x: true }}
          />
        </GlobalS.PageContentCard>
      </Row>
    </PageWrapper>
  );
};
